import React, { useState, useEffect, useMemo } from 'react';
import { Configuration, FrontendApi, LoginFlow } from '@ory/client';
import Config from 'src/common/Config';
import { Button, TextInput } from 'src/components';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { Center, Divider, Stack, Flex } from '@mantine/core';
import { OidcSection } from 'src/components/convenience/auth/OidcSection';
import { TermsAndPrivacy } from 'src/auth/components/TermsAndPrivacy';
import { z } from 'zod';

const ory = new FrontendApi(
  new Configuration({
    basePath: Config.getAuthBasePath(),
    baseOptions: {
      withCredentials: true, // important for cookies
    },
  }),
);

const emailSchema = z.string().email();

type Props = {
  flow: LoginFlow;
  setFlow: (flow: LoginFlow) => void;
  setError: (error: string | undefined) => void;
  resetFlow: () => Promise<unknown>;
};

export default function LoginTabContent({ flow, setFlow, setError, resetFlow }: Props) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const isEmailValid = emailSchema.safeParse(email).success;

  const emailNode = useMemo(() => {
    return flow.ui.nodes.find(node => {
      return (
        node.group === 'identifier_first' &&
        node.meta?.label?.context &&
        'name' in node.meta.label.context &&
        node.meta?.label?.context.name === 'traits.email'
      );
    });
  }, [flow]);

  useEffect(() => {
    if (emailNode && 'value' in emailNode.attributes && emailNode.attributes.value) {
      setEmail(String(emailNode.attributes.value));
    }
  }, [emailNode]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);
    const values: Record<string, unknown> = {};
    formData.forEach((value, key) => {
      values[key] = value.toString();
    });

    try {
      const { data } = await ory.updateLoginFlow({
        flow: flow.id,
        updateLoginFlowBody: {
          method: 'password',
          identifier: values.identifier as string,
          password: values.password as string,
          csrf_token: values.csrf_token as string,
        },
      });

      if (data.session) {
        window.location.href = '/';
      } else if ('ui' in data) {
        setFlow(data as unknown as LoginFlow);
      }
    } catch (e: unknown) {
      if (
        e &&
        typeof e === 'object' &&
        'response' in e &&
        e.response &&
        typeof e.response === 'object' &&
        'data' in e.response &&
        e.response.data &&
        typeof e.response.data === 'object'
      ) {
        if (
          'status' in e.response &&
          e.response?.status === 400 &&
          'ui' in e.response.data &&
          e.response.data?.ui
        ) {
          setFlow(e.response.data as LoginFlow);
          setError(undefined);
          return;
        }

        if (
          'status' in e.response &&
          e.response?.status === 422 &&
          'redirect_browser_to' in e.response.data &&
          typeof e.response.data?.redirect_browser_to === 'string'
        ) {
          window.location.href = e.response.data.redirect_browser_to;
          return;
        }
      } else {
        notifyAndLogError('Login Error', 'An error occurred during login.', e);
        setError('Login failed. Please try again.');
      }
    }
  };

  // needed for hidden fields like csrf_token
  const hiddenNodes = flow.ui.nodes
    .filter(node => 'type' in node.attributes && node.attributes.type === 'hidden')
    .map(node => {
      const attributes = node.attributes as unknown as Record<string, string>;
      return (
        <input
          key={`${attributes.name}-{attributes.type}`}
          name={attributes.name || ''}
          type={attributes.type}
          defaultValue={attributes.value}
          required={!!attributes.required}
          disabled={!!attributes.disabled}
        />
      );
    });

  const passwordNode = flow.ui.nodes.find(node => {
    return (
      node.group === 'default' && 'name' in node.attributes && node.attributes.name === 'password'
    );
  });

  // this with the key in the email text input is a bit of a hack to force re-mounting
  // and preventing the email field to be highlighted in yellow by Dashlane after pressing continue
  const emailValueProps =
    emailNode && 'value' in emailNode.attributes && emailNode.attributes.value
      ? { value: emailNode.attributes.value }
      : {};

  const errorMessage = flow.ui.messages
    ?.filter(m => m.type === 'error')
    .map(m =>
      flow.ui.messages?.length && flow.ui.messages?.length > 1 ? (
        <>
          <br />• {m.text}
        </>
      ) : (
        m.text
      ),
    );

  const oidcNodes = flow.ui.nodes.filter(node => node.group === 'oidc');

  const csrfToken =
    (
      flow.ui.nodes.find(node => 'name' in node.attributes && node.attributes.name === 'csrf_token')
        ?.attributes as { value?: string }
    )?.value || '';

  return (
    <Stack gap="lg">
      <form onSubmit={handleSubmit}>
        {emailNode && (
          <>
            <TextInput
              key={`email-${!!emailNode}-${!!passwordNode}`}
              name={'name' in emailNode.attributes ? emailNode.attributes.name : undefined}
              disabled={!!passwordNode}
              mt={oidcNodes.length > 0 ? 'md' : 'xl'}
              mb="md"
              required
              label="Email"
              placeholder={'name@example.com'}
              autoFocus
              onChange={e => setEmail(e.currentTarget.value)}
              {...emailValueProps}
            />
            {/*
              When the test input is disabled, it's value is not passed in as part of the submit
              action. To work around this, we add a hidden input with the same name and value.
            */}
            {passwordNode && 'value' in emailNode.attributes && (
              <input
                type="hidden"
                name={'name' in emailNode.attributes ? emailNode.attributes.name : undefined}
                value={emailNode.attributes.value}
              />
            )}
            {hiddenNodes}
          </>
        )}
        {emailNode && passwordNode && (
          <TextInput
            mt="xl"
            mb="md"
            required
            label="Password"
            placeholder={'Enter your password'}
            type={'password'}
            name={'name' in passwordNode.attributes ? passwordNode.attributes.name : undefined}
            error={errorMessage}
            onChange={e => setPassword(e.currentTarget.value)}
          />
        )}
        <Button
          type={'submit'}
          fullWidth
          disabled={passwordNode ? password.trim() === '' : !isEmailValid}
        >
          {passwordNode ? 'Sign In' : 'Continue'}
        </Button>
        <Center mt="xxs">
          <Flex gap="xxxs" direction="row">
            <Button variant={'secondary'} to={'/recovery'} size={'xs'}>
              Forgot your password?
            </Button>
            {passwordNode && (
              <Button variant={'secondary'} onClick={resetFlow} size={'xs'}>
                Want to switch accounts?
              </Button>
            )}
          </Flex>
        </Center>
      </form>

      <Divider label="or" labelPosition="center" />

      <OidcSection nodes={oidcNodes} formAction={flow.ui.action} csrfToken={csrfToken} />

      <TermsAndPrivacy />
    </Stack>
  );
}
